import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { SelectButton } from 'primereact/selectbutton'
import {
  OperatingRoomEvolutionDTO,
  OperatingRoomEvolutionPointDTO,
  OperatingRoomFilterDTO,
  OperatingRoomSummaryDTO,
} from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { SectionState } from '../../UrgencySummary/useSectionData'
import {
  averagePerPoint,
  OPERATING_ROOM_MEASURES,
  OperatingRoomMeasure,
  occupancyPeaks,
} from '../operatingRoomEvolutionModel'
import OperatingRoomDaySidebar from './OperatingRoomDaySidebar'
import OperatingRoomEvolutionChart from './OperatingRoomEvolutionChart'
import OperatingRoomPeakChips from './OperatingRoomPeakChips'

const MEASURE_LABEL_ID: Record<OperatingRoomMeasure, string> = {
  surgeries: 'OperatingRoomIndicatorsKpiSurgeries',
  hours: 'OperatingRoomIndicatorsKpiHours',
  occupancy: 'OperatingRoomIndicatorsKpiOccupancy',
}

interface OperatingRoomEvolutionProps {
  summary: OperatingRoomSummaryDTO
  /** La serie de la pestaña (consulta aparte, recién al abrirla). */
  evolution: SectionState<OperatingRoomEvolutionDTO>
  /** Período y filtros aplicados de la pantalla. */
  filters: OperatingRoomFilterDTO
  /** La medida elegida vive en la pantalla: no se pierde al cambiar de pestaña ni de filtros. */
  measure: OperatingRoomMeasure
  onMeasureChange: (measure: OperatingRoomMeasure) => void
  /** «Ver solo este día» del panel: aplica las fechas como período de la pantalla. */
  onViewRange: (from: string, to: string) => void
  /** «Ver cirugías» del panel: las mismas fechas, y la lista. */
  onViewList: (from: string, to: string) => void
}

/**
 * Pestaña «Evolución»: las cirugías, las horas o la ocupación día por día (semana por semana si el período pasa de 90 días), con
 * la línea de promedio del período, los picos de la ocupación (chips «Picos» junto al gráfico) y, al tocar un punto, el panel
 * lateral con su detalle. «Cirugías | Horas | Ocupación» son el mismo dato en otra vista (SelectButton): no llevan rótulo visible,
 * solo nombre accesible. La serie y el panel se piden aparte (`/evolution` y `/day`).
 */
export default function OperatingRoomEvolution({
  summary, evolution, filters, measure, onMeasureChange, onViewRange, onViewList,
}: OperatingRoomEvolutionProps) {
  const intl = useIntl()
  const shownData = evolution.data ?? evolution.stale
  const points = shownData?.points ?? []
  const granularity = shownData?.granularity ?? 'DAY'
  const refreshing = evolution.status === 'loading'

  const analysis = useMemo(
    () => (measure === 'occupancy' ? occupancyPeaks(points, summary.asOf) : null),
    [points, measure, summary.asOf],
  )
  const average = useMemo(() => {
    if (measure === 'occupancy') return summary.current.occupancyPct
    return shownData ? averagePerPoint(shownData, measure === 'hours' ? 'hours' : 'surgeries', summary.asOf) : null
  }, [shownData, measure, summary.current.occupancyPct, summary.asOf])

  // ── Panel del día ──
  const [detail, setDetail] = useState<OperatingRoomEvolutionPointDTO | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  /** Abre el panel de un punto; al cerrarlo el foco vuelve a `opener` (el gráfico o el chip). */
  const openPoint = useCallback((point: OperatingRoomEvolutionPointDTO, opener: HTMLElement | null) => {
    openerRef.current = opener
    setDetail(point)
  }, [])
  const closeDetail = useCallback(() => {
    setDetail(null)
    const opener = openerRef.current
    openerRef.current = null
    // Cuando el panel ya se cerró; si el gráfico o el chip ya no están, no hay a dónde volver
    if (opener) window.setTimeout(() => { if (opener.isConnected) opener.focus() }, 0)
  }, [])
  const viewRange = useCallback((from: string, to: string) => {
    onViewRange(from, to)
    closeDetail()
  }, [onViewRange, closeDetail])
  const viewList = useCallback((from: string, to: string) => {
    onViewList(from, to)
    setDetail(null)
    openerRef.current = null
  }, [onViewList])

  const measureOptions = OPERATING_ROOM_MEASURES.map((value) => ({ value, label: intl.formatMessage({ id: MEASURE_LABEL_ID[value] }) }))
  const chartLabel = `${intl.formatMessage({ id: granularity === 'DAY' ? 'OperatingRoomIndicatorsEvolutionLabelDay' : 'OperatingRoomIndicatorsEvolutionLabelWeek' })} · ${intl.formatMessage({ id: MEASURE_LABEL_ID[measure] })}`

  return (
    <section className="nv-or-evolution" aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsTabEvolution' })} aria-busy={refreshing}>
      <div className="nv-or-evolution__controls">
        <SelectButton
          value={measure}
          options={measureOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(event) => { if (event.value) onMeasureChange(event.value as OperatingRoomMeasure) }}
          aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsMeasureBy' })}
        />
      </div>

      {refreshing && shownData === null && (
        <span className="nv-loading" role="status">
          <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
          <span>{intl.formatMessage({ id: 'OperatingRoomIndicatorsLoading' })}</span>
        </span>
      )}
      {evolution.status === 'failed' && (
        <div className="nv-or-failed" role="alert">
          <p className="nv-note">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {intl.formatMessage({ id: 'OperatingRoomIndicatorsEvolutionError' })}
          </p>
          <Button type="button" link label={intl.formatMessage({ id: 'OperatingRoomIndicatorsRetry' })} onClick={evolution.retry} />
        </div>
      )}
      {shownData !== null && evolution.status !== 'failed' && (points.length === 0
        ? <p className="nv-muted">{intl.formatMessage({ id: 'OperatingRoomIndicatorsEmpty' })}</p>
        : (
          <div className={`nv-or-evolution__content${refreshing ? ' nv-or-blurred' : ''}`}>
            <OperatingRoomEvolutionChart
              points={points}
              granularity={granularity}
              asOf={summary.asOf}
              measure={measure}
              ariaLabel={chartLabel}
              average={average}
              analysis={analysis}
              onOpen={openPoint}
            />
            {analysis && <OperatingRoomPeakChips points={points} granularity={granularity} analysis={analysis} onOpen={openPoint} />}
          </div>
        ))}

      <OperatingRoomDaySidebar
        point={detail}
        granularity={granularity}
        filters={filters}
        onViewRange={viewRange}
        onViewList={viewList}
        onHide={closeDetail}
      />
    </section>
  )
}
